import { useEffect, useRef } from 'react';
import { Animated, Easing, View } from 'react-native';
import THEME from '../../../config/theme';
import { useCustomTheme } from '../../../contexts/useCustomTheme';
import { hexToRgba } from '../../../utils/colorUtils';

// Respeita quem pediu menos animação no sistema. Fora do navegador (ou em
// browser antigo) `matchMedia` não existe: o padrão é animar.
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Esqueleto da lista de comentários.
 *
 * Substitui o ActivityIndicator que ficava em `position: absolute` cobrindo a
 * coluna inteira — inclusive o campo de escrever — e que reaparecia a cada
 * atualização da lista. O esqueleto ocupa exatamente o espaço que os
 * comentários vão ocupar, então nada salta de lugar quando os dados chegam.
 *
 * Só aparece antes do PRIMEIRO snapshot (`comments === undefined`). Depois
 * disso, lista vazia significa "não há comentários", não "carregando".
 */
const CommentSkeleton = ({ rows = 3, compact = false }) => {
  const { customColor } = useCustomTheme();
  const pulse = useRef(new Animated.Value(0.5)).current;

  const textColor = customColor?.text?.app || THEME.COLORS.TEXT_MAIN;
  const base = hexToRgba(textColor, 0.08) || '#E5E7EB';

  useEffect(() => {
    if (prefersReducedMotion()) return;

    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 700,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0.5,
          duration: 700,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ])
    );

    animation.start();
    return () => animation.stop();
  }, [pulse]);

  const avatarSize = compact ? 32 : 36;

  return (
    <View accessibilityLabel="Carregando comentários">
      {Array.from({ length: rows }).map((_, index) => (
        <Animated.View
          key={index}
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            paddingVertical: 12,
            opacity: pulse,
          }}
        >
          <View
            style={{
              width: avatarSize,
              height: avatarSize,
              borderRadius: avatarSize / 2,
              backgroundColor: base,
              marginRight: 12,
            }}
          />
          <View style={{ flex: 1 }}>
            <View
              style={{
                width: '45%',
                height: 10,
                borderRadius: 5,
                backgroundColor: base,
              }}
            />
            <View
              style={{
                // A última linha mais curta dá a impressão de parágrafo, em vez
                // de blocos idênticos empilhados.
                width: index === rows - 1 ? '60%' : '85%',
                height: 10,
                borderRadius: 5,
                backgroundColor: base,
                marginTop: 8,
              }}
            />
          </View>
        </Animated.View>
      ))}
    </View>
  );
};

export default CommentSkeleton;
